// The floating bottom bar on phones: Home, Chat, Inbox and More (the apps grid: My Tasks, Docs,
// Spaces, Planner…), with the round + button for Create beside it. The highlight slides (and stretches
// like a drop of liquid) to the tab you pick.
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { Apps, CalendarRange, FileText, House, Inbox, Layers, MessagesSquare, NotebookPen, Orbit, Plus, Timer, UserCheck, UsersRound, type IconComponent } from '@/components/ui/icons';
import { useDb, useMe } from '@/lib/store';
import { useWorkspaceId } from '@/lib/nav';
import { useIsGuest } from '@/lib/roles';
import { useInboxUnread } from '@/shell/Rail';
import { openSheet } from './sheets';

interface App {
  key: string;
  label: string;
  icon: IconComponent;
  /** tile color */
  color: string;
  /** page under /:ws/, or a sheet */
  path?: string;
  sheet?: 'notepad';
  members?: boolean;
}

export const APPS: App[] = [
  { key: 'my-tasks', label: 'My Tasks', icon: UserCheck, color: '#14b8a6', path: 'my-tasks' },
  { key: 'docs', label: 'Docs', icon: FileText, color: '#3b82f6', path: 'docs' },
  { key: 'spaces', label: 'Spaces', icon: Orbit, color: '#8b5cf6', path: 'spaces' },
  { key: 'planner', label: 'Planner', icon: CalendarRange, color: '#ec4899', path: 'planner' },
  { key: 'notepad', label: 'Notepad', icon: NotebookPen, color: '#f59e0b', sheet: 'notepad' },
  { key: 'everything', label: 'All Tasks', icon: Layers, color: '#6366f1', path: 'everything', members: true },
  { key: 'teams', label: 'Teams', icon: UsersRound, color: '#10b981', path: 'teams' },
  { key: 'timesheets', label: 'Timesheets', icon: Timer, color: '#ef4444', path: 'timesheets', members: true },
];

/** Unread messages in the conversations I'm in (thread replies aside). */
function useChatUnread() {
  const me = useMe();
  const ws = useWorkspaceId();
  return useDb((d) => {
    const lastRead: Record<string, number> = {};
    for (const r of Object.values(d.channelReads)) if (r.userId === me) lastRead[r.channelId] = r.lastReadAt;
    let n = 0;
    for (const m of Object.values(d.messages)) {
      if (m.parentId || m.authorId === me || m.createdAt <= (lastRead[m.channelId] ?? 0)) continue;
      const ch = d.channels[m.channelId];
      if (ch?.workspaceId === ws && ch.memberIds.includes(me)) n++;
    }
    return n;
  });
}

const TABS = ['home', 'chat', 'inbox'] as const;

export function TabBar() {
  const ws = useWorkspaceId();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const unread = useInboxUnread();
  const chatUnread = useChatUnread();
  const guest = useIsGuest();
  const [gridOpen, setGridOpen] = useState(false);
  const seg = pathname.split('/')[2] ?? '';
  const apps = APPS.filter((a) => !(guest && a.members));
  const bar = useRef<HTMLDivElement>(null);
  const lens = useRef<HTMLSpanElement>(null);
  const lensAt = useRef<number | null>(null);
  const tabIndex = (TABS as readonly string[]).indexOf(seg);
  const active = gridOpen ? 3 : tabIndex >= 0 ? tabIndex : apps.some((a) => a.path === seg) ? 3 : -1;

  useEffect(() => setGridOpen(false), [pathname]);

  // slide the lens to the active tab, stretching on the way like a drop of liquid
  useLayoutEffect(() => {
    const el = lens.current;
    const btn = bar.current?.querySelectorAll<HTMLElement>('.m-tab')[active];
    if (!el) return;
    if (!btn) {
      el.style.opacity = '0';
      lensAt.current = null;
      return;
    }
    const x = btn.offsetLeft;
    el.style.width = `${btn.offsetWidth}px`;
    el.style.opacity = '1';
    const from = lensAt.current;
    el.style.transform = `translateX(${x}px)`;
    if (from !== null && from !== x && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      const mid = from + (x - from) * 0.55;
      el.animate(
        [
          { transform: `translateX(${from}px) scale(1, 1)` },
          { transform: `translateX(${from + (x - from) * 0.12}px) scale(1.08, 0.94)`, offset: 0.12 },
          { transform: `translateX(${mid}px) scale(1.42, 0.82)`, offset: 0.45 },
          { transform: `translateX(${x}px) scale(0.92, 1.08)`, offset: 0.78 },
          { transform: `translateX(${x}px) scale(1, 1)` },
        ],
        { duration: 560, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
      );
    }
    lensAt.current = x;
  }, [active]);

  // keep the highlight on its tab when the bar changes width (rotating the phone)
  useEffect(() => {
    const el = bar.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => {
      const btn = el.querySelectorAll<HTMLElement>('.m-tab')[active];
      if (!btn || !lens.current) return;
      lens.current.style.transform = `translateX(${btn.offsetLeft}px)`;
      lens.current.style.width = `${btn.offsetWidth}px`;
      lensAt.current = btn.offsetLeft;
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [active]);

  const openApp = (a: App) => {
    setGridOpen(false);
    if (a.path) navigate(`/${ws}/${a.path}`);
    else if (a.sheet) openSheet(a.sheet);
  };
  const tab = (path: string, icon: React.ReactNode, label: string, badge?: number) => (
    <button type="button" className={clsx('m-tab', { active: seg === path && !gridOpen })} onClick={() => navigate(`/${ws}/${path}`)} aria-label={label}>
      {icon}
      {!!badge && <span className="m-tab-badge">{badge > 99 ? '99+' : badge}</span>}
    </button>
  );

  return (
    <>
      {gridOpen && <div className="m-apps-scrim" onClick={() => setGridOpen(false)} />}
      <div className={clsx('m-apps', { open: gridOpen })} aria-hidden={!gridOpen}>
        <div className="m-apps-grid">
          {apps.map((a) => (
            <button key={a.key} type="button" className="m-app-tile" onClick={() => openApp(a)} tabIndex={gridOpen ? 0 : -1}>
              <span className="m-app-icon" style={{ '--c': a.color } as React.CSSProperties}>
                <a.icon size={24} />
              </span>
              <span className="m-app-label">{a.label}</span>
            </button>
          ))}
        </div>
      </div>
      <nav className="m-tabbar" aria-label="Main">
        <div ref={bar} className="m-tabs">
          <span ref={lens} className="m-lens" aria-hidden="true" />
          {tab('home', <House size={22} />, 'Home')}
          {tab('chat', <MessagesSquare size={22} />, 'Chat', chatUnread)}
          {tab('inbox', <Inbox size={22} />, 'Inbox', unread)}
          <button type="button" className={clsx('m-tab', { active: active === 3 })} onClick={() => setGridOpen((o) => !o)} aria-label="More" aria-expanded={gridOpen}>
            <Apps size={22} />
          </button>
        </div>
        <button type="button" className="m-fab" onClick={() => openSheet('create')} aria-label="Create">
          <Plus size={24} />
        </button>
      </nav>
    </>
  );
}
